import { useEffect, useState } from 'react';
import { CheckCircle, Music, Clock, MapPin, Users, ChevronDown, ChevronUp, Calendar } from 'lucide-react';
import { api } from '../api';
import { cn } from '../lib/utils';

const DAYS_FR = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];

export default function Register() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitted, setSubmitted] = useState(false);
  const [submitResult, setSubmitResult] = useState(null);
  const [sending, setSending] = useState(false);
  const [selectedCourses, setSelectedCourses] = useState([]);
  const [showCourses, setShowCourses] = useState(false);
  const [form, setForm] = useState({
    first_name: '', last_name: '', email: '', phone: '',
    instagram: '', dance_role: '', dance_background: '', experience_years: 0, message: ''
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    api.get('/public/registration-config')
      .then(d => setConfig(d))
      .catch(() => setConfig({ active: false, message: '', courses: [] }))
      .finally(() => setLoading(false));
  }, []);

  const toggle = field => v => setForm(f => ({ ...f, [field]: v }));

  const validate = () => {
    const e = {};
    if (!form.first_name.trim()) e.first_name = 'Obligatoire';
    if (!form.last_name.trim()) e.last_name = 'Obligatoire';
    if (!form.email.trim() || !form.email.includes('@')) e.email = 'Email invalide';
    return e;
  };

  const handleSubmit = async e => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setSending(true);
    try {
      const res = await api.post('/public/register', { ...form, experience_years: Number(form.experience_years), course_ids: selectedCourses });
      setSubmitResult(res);
      setSubmitted(true);
    } catch (err) {
      setErrors({ submit: err?.detail || 'Une erreur est survenue, réessayez.' });
    } finally { setSending(false); }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="w-8 h-8 border-4 border-[#0288D1] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (config?.active === false) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
      <div className="text-center max-w-md">
        <div className="w-14 h-14 bg-slate-200 rounded-2xl flex items-center justify-center mx-auto mb-4">
          <Music size={24} className="text-slate-400" />
        </div>
        <h1 className="text-xl font-bold text-slate-800 mb-2">Inscriptions fermées</h1>
        <p className="text-slate-500 text-sm">Les inscriptions ne sont pas ouvertes pour le moment. Revenez bientôt !</p>
      </div>
    </div>
  );

  if (submitted) return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 to-slate-800 px-4">
      <div className="bg-white rounded-3xl p-10 max-w-md w-full text-center shadow-2xl">
        <div className={cn(
          "w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-5",
          submitResult?.status === 'approved' ? "bg-emerald-100" : "bg-[#0288D1]/10"
        )}>
          <CheckCircle size={32} className={submitResult?.status === 'approved' ? "text-emerald-500" : "text-[#0288D1]"} />
        </div>
        <h2 className="text-2xl font-extrabold text-slate-900 mb-3">
          {submitResult?.status === 'approved' ? 'Inscription confirmée !' : 'Demande envoyée !'}
        </h2>
        <p className="text-slate-500 text-sm leading-relaxed">{submitResult?.message}</p>
        <p className="text-xs text-slate-400 mt-6">Vous pouvez fermer cette fenêtre.</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 py-10 px-4">
      <div className="max-w-lg mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-14 h-14 bg-[#0288D1] rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Music size={26} color="white" />
          </div>
          <h1 className="text-3xl font-extrabold text-white mb-1">Inscription aux cours</h1>
          <p className="text-slate-400 text-sm">Bachata & Brazilian Zouk</p>
        </div>

        {/* Welcome message */}
        {config?.message && (
          <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-5 mb-6">
            <p className="text-white/90 text-sm leading-relaxed whitespace-pre-line">{config.message}</p>
          </div>
        )}

        {/* Course selection */}
        {config?.courses?.length > 0 && (
          <div className="bg-white rounded-2xl shadow-xl mb-5">
            <button onClick={() => setShowCourses(v => !v)}
              className="w-full flex items-center justify-between px-5 py-4 text-left">
              <div className="flex items-center gap-2">
                <Calendar size={16} className="text-[#0288D1]" />
                <span className="font-semibold text-slate-800 text-sm">
                  Cours souhaités
                  {selectedCourses.length > 0 && (
                    <span className="ml-2 px-2 py-0.5 rounded-full bg-[#0288D1] text-white text-xs">{selectedCourses.length}</span>
                  )}
                </span>
              </div>
              {showCourses ? <ChevronUp size={16} className="text-slate-400" /> : <ChevronDown size={16} className="text-slate-400" />}
            </button>
            {showCourses && (
              <div className="border-t border-slate-100 p-3 space-y-2">
                {config.courses.map(course => {
                  const selected = selectedCourses.includes(course.id);
                  return (
                    <button key={course.id}
                      onClick={() => setSelectedCourses(s => selected ? s.filter(i => i !== course.id) : [...s, course.id])}
                      className={cn(
                        "w-full text-left px-4 py-3 rounded-xl border transition-all",
                        selected ? "bg-[#0288D1]/10 border-[#0288D1]/30" : "bg-slate-50 border-slate-200 hover:border-[#0288D1]/30"
                      )}>
                      <div className="flex items-center gap-2 mb-1">
                        <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: course.color || '#0288D1' }} />
                        <span className="font-semibold text-slate-800 text-sm">{course.name}</span>
                        {selected && <span className="ml-auto text-[#0288D1] text-xs font-bold">✓</span>}
                      </div>
                      <div className="flex flex-wrap gap-3 text-xs text-slate-400 pl-4">
                        <span className="flex items-center gap-1"><Clock size={10} />{DAYS_FR[course.day_of_week]} {course.start_time}</span>
                        {course.location?.venue_name && <span className="flex items-center gap-1"><MapPin size={10} />{course.location.venue_name}</span>}
                        {course.level?.length > 0 && <span className="flex items-center gap-1"><Users size={10} />{course.level.join(', ')}</span>}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Registration form */}
        <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-xl p-6 space-y-4">
          <h2 className="font-bold text-slate-800 text-base mb-2">Vos informations</h2>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Prénom *" error={errors.first_name}>
              <input value={form.first_name} onChange={e => toggle('first_name')(e.target.value)}
                placeholder="Prénom" data-testid="register-firstname"
                className={inputCls(errors.first_name)} />
            </Field>
            <Field label="Nom *" error={errors.last_name}>
              <input value={form.last_name} onChange={e => toggle('last_name')(e.target.value)}
                placeholder="Nom" data-testid="register-lastname"
                className={inputCls(errors.last_name)} />
            </Field>
          </div>

          <Field label="Email *" error={errors.email}>
            <input type="email" value={form.email} onChange={e => toggle('email')(e.target.value)}
              placeholder="votre@email.com" data-testid="register-email"
              className={inputCls(errors.email)} />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Téléphone">
              <input value={form.phone} onChange={e => toggle('phone')(e.target.value)}
                placeholder="+41 79 000 00 00" data-testid="register-phone"
                className={inputCls()} />
            </Field>
            <Field label="Instagram">
              <input value={form.instagram} onChange={e => toggle('instagram')(e.target.value)}
                placeholder="@votre_pseudo" data-testid="register-instagram"
                className={inputCls()} />
            </Field>
          </div>

          <Field label="Votre rôle de danse">
            <div className="flex gap-2">
              {[
                { value: 'follower', label: 'Follower', activeClass: 'bg-pink-500 text-white border-pink-500' },
                { value: 'leader', label: 'Leader', activeClass: 'bg-[#0288D1] text-white border-[#0288D1]' },
              ].map(r => (
                <button key={r.value} type="button"
                  onClick={() => toggle('dance_role')(form.dance_role === r.value ? '' : r.value)}
                  data-testid={`register-role-${r.value}`}
                  className={cn(
                    'flex-1 h-10 rounded-xl border font-semibold text-sm transition-all',
                    form.dance_role === r.value ? r.activeClass : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'
                  )}>
                  {r.label}
                </button>
              ))}
            </div>
          </Field>

          <Field label="Votre danse actuelle">            <select value={form.dance_background} onChange={e => toggle('dance_background')(e.target.value)}
              data-testid="register-dance-bg"
              className={inputCls()}>
              <option value="">— Sélectionner —</option>
              <option value="Bachata Sensual">Bachata Sensual</option>
              <option value="Bachata Tradicional">Bachata Tradicional</option>
              <option value="Brazilian Zouk">Brazilian Zouk</option>
              <option value="Salsa">Salsa</option>
              <option value="Autre">Autre</option>
              <option value="Débutant complet">Débutant complet</option>
            </select>
          </Field>

          <Field label="Depuis combien de temps dansez-vous ? (années)">
            <input type="number" min="0" max="50" value={form.experience_years}
              onChange={e => toggle('experience_years')(e.target.value)}
              data-testid="register-experience" className={inputCls()} />
          </Field>

          <Field label="Message (facultatif)">
            <textarea value={form.message} onChange={e => toggle('message')(e.target.value)}
              placeholder="Parlez-nous de vos objectifs, questions, disponibilités…"
              rows={3} data-testid="register-message"
              className={inputCls() + " resize-none"} />
          </Field>

          {errors.submit && (
            <p className="text-red-500 text-xs bg-red-50 px-3 py-2 rounded-lg">{errors.submit}</p>
          )}

          <button type="submit" disabled={sending} data-testid="register-submit"
            className="w-full h-11 rounded-xl bg-[#0288D1] text-white font-bold text-sm hover:bg-[#0277bd] transition-colors disabled:opacity-50 mt-2">
            {sending ? 'Envoi en cours…' : 'Envoyer ma demande'}
          </button>
          <p className="text-xs text-slate-400 text-center">
            Vos données ne seront utilisées que pour la gestion des cours.
          </p>
        </form>
      </div>
    </div>
  );
}

function Field({ label, error, children }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-slate-600 mb-1">{label}</label>
      {children}
      {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
    </div>
  );
}

const inputCls = (err) => cn(
  'w-full h-9 px-3 border rounded-lg text-sm bg-white focus:outline-none focus:ring-2 transition-colors',
  err ? 'border-red-300 focus:ring-red-200 focus:border-red-400' : 'border-slate-200 focus:ring-[#0288D1]/20 focus:border-[#0288D1]'
);
